import type { PickerProps } from 'antd/es/date-picker/generatePicker';
import generatePicker from 'antd/es/date-picker/generatePicker';
import type { Moment } from 'moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import React, { forwardRef, useId } from 'react';
import styled from 'styled-components';
import useT from '@tools/ui-hooks/useT';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import { inputFieldBorderStyle } from '../_shared/InputField';

const AntDatePicker = generatePicker<Moment>(momentGenerateConfig);

type AntDatePickerFieldProps = Omit<
  Pick<
    PickerProps<Moment>,
    | 'value'
    | 'defaultValue'
    | 'placeholder'
    | 'disabled'
    | 'bordered'
    | 'picker'
    | 'format'
    | 'disabledDate'
    | 'renderExtraFooter'
    | 'open'
    | 'onOpenChange'
    | 'onChange'
    | 'onFocus'
    | 'onBlur'
    | 'className'
    | 'id'
    | 'name'
    | 'aria-label'
    | 'aria-labelledby'
  >,
  'picker'
>;

export type DatePickerProps = AntDatePickerFieldProps &
  Partial<{
    picker: 'date' | 'month' | 'year';
    readOnly: boolean;
    status: 'error' | 'default';
  }>;

const DatePickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ForwardedAntDatePicker = forwardRef<
  React.ElementRef<typeof AntDatePicker>,
  any
>((props, ref) => <AntDatePicker {...props} ref={ref} />);

const StyledDropdown = styled.div`
  .ant-picker-dropdown {
    .ant-picker-panel-container {
      background: ${theme.dime.colors.surface.primary};
      border-radius: ${theme.dime.radius.m};
      box-shadow: 0px 0px 7px rgba(0, 0, 0, 0.08);
    }

    .ant-picker-header {
      border-bottom: 1px solid ${theme.dime.colors.border.static};

      button {
        color: ${theme.dime.colors.icon.primary};
      }

      .ant-picker-header-view {
        line-height: ${theme.dime.fonts.lineHeights.md};
        color: ${theme.dime.colors.text.primary};
      }
    }

    .ant-picker-cell {
      padding: ${theme.dime.spacing.s} 0;
      color: ${theme.dime.colors.text.disabled};

      .ant-picker-cell-inner {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: ${theme.dime.radius.s};
        line-height: ${theme.dime.fonts.lineHeights.md};
      }

      &.ant-picker-cell-in-view {
        color: ${theme.dime.colors.text.primary};
      }

      &:not(.ant-picker-cell-disabled):hover .ant-picker-cell-inner {
        background: ${theme.dime.colors.surface.secondaryHover};
      }

      &.ant-picker-cell-today .ant-picker-cell-inner::before {
        border-color: ${theme.dime.colors.border.interactiveSelected};
      }

      &.ant-picker-cell-selected .ant-picker-cell-inner {
        background: ${theme.dime.colors.fill.brandPrimary};
        color: ${theme.dime.colors.text.inverse};
      }

      &.ant-picker-cell-disabled .ant-picker-cell-inner {
        color: ${theme.dime.colors.text.disabled};
      }
    }

    .ant-picker-footer {
      border-top: 1px solid ${theme.dime.colors.border.static};
    }
  }
`;

const StyledDatePicker = styled(ForwardedAntDatePicker)<{
  $error: boolean;
  $disabled: boolean;
  $readOnly: boolean;
  $bordered: boolean;
}>`
  &&.ant-picker {
    ${inputFieldBorderStyle}
    border-radius: ${theme.dime.radius.m};
    padding: 0
      ${({ $bordered }) =>
        $bordered ? theme.dime.spacing.l : theme.dime.spacing.s};
    height: ${({ $bordered }) =>
      $bordered ? theme.dime.spacing['3xl'] : theme.dime.spacing['2xl']};
    box-shadow: none;
    width: 100%;
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

    ${({ $bordered }) =>
      !$bordered &&
      `
      border-color: transparent;
      background-color: transparent;

      &:hover,
      &:focus-within {
        border-color: transparent;
      }
    `}
  }

  &&.ant-picker-disabled input::placeholder {
    color: ${theme.dime.colors.text.disabled};
  }

  &&.ant-picker input {
    cursor: ${({ $disabled, $readOnly }) =>
      $disabled || $readOnly ? 'not-allowed' : 'text'};
    background-color: transparent;
    line-height: ${theme.dime.fonts.lineHeights.md};
  }
`;

export const DatePicker = forwardRef<
  React.ElementRef<typeof AntDatePicker>,
  DatePickerProps
>(
  (
    {
      placeholder,
      disabled = false,
      readOnly = false,
      bordered = true,
      format = 'MM/DD/YYYY',
      open,
      className = '',
      id,
      'aria-labelledby': ariaLabelledBy,
      status = 'default',
      ...rest
    },
    ref
  ) => {
    const { t } = useT();
    const hasError = status === 'error';
    const resolvedPlaceholder = placeholder ?? t('datePicker.placeholder');
    const generatedId = useId();
    const inputId = id ?? generatedId;

    return (
      <DatePickerWrapper className={className}>
        <StyledDropdown>
          <StyledDatePicker
            {...rest}
            ref={ref}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            inputReadOnly={readOnly}
            open={readOnly ? false : open}
            format={format}
            id={inputId}
            aria-labelledby={ariaLabelledBy}
            allowClear={
              disabled || readOnly
                ? false
                : {
                    clearIcon: (
                      <IconWrap
                        type={IconType.Close}
                        size={IconSize.Small}
                        style={{ color: theme.dime.colors.icon.primary }}
                      />
                    )
                  }
            }
            getPopupContainer={(triggerNode: HTMLElement) =>
              triggerNode.parentElement ?? document.body
            }
            suffixIcon={
              <IconWrap
                type={IconType.Calendar}
                size={IconSize.Small}
                disabled={disabled || readOnly}
                style={
                  disabled || readOnly
                    ? undefined
                    : { color: theme.dime.colors.icon.primary }
                }
              />
            }
            $error={hasError}
            $disabled={disabled}
            $readOnly={readOnly}
            $bordered={bordered}
          />
        </StyledDropdown>
      </DatePickerWrapper>
    );
  }
);

DatePicker.displayName = 'DatePicker';
